/**
 * Display-layer artist grouping for the local library artists view.
 * Merges tag variants and typos — does not rewrite metadata on disk.
 */

const COLLABORATOR_SUFFIX =
  /\s+(?:(?:&|and)\s+(?:his|her|their|the)\s+.+|(?:feat\.|ft\.|featuring)\s+.+)$/i;

const SOLO_ARTISTS = new Set(['unknown artist', 'various artists']);

/** Lowercase, collapse whitespace, strip punctuation and diacritics. */
export function normalizeArtistName(name) {
  return String(name || '')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function stripCollaboratorSuffix(name) {
  return String(name || '').replace(COLLABORATOR_SUFFIX, '').trim();
}

export function hasCollaboratorSuffix(name) {
  return COLLABORATOR_SUFFIX.test(String(name || ''));
}

/** Damerau-Levenshtein — transpositions cost 1 (Holiday/Hoilday). */
export function damerauLevenshtein(a, b) {
  const s = a || '';
  const t = b || '';
  const m = s.length;
  const n = t.length;
  if (m === 0) return n;
  if (n === 0) return m;

  const d = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i += 1) d[i][0] = i;
  for (let j = 0; j <= n; j += 1) d[0][j] = j;

  for (let i = 1; i <= m; i += 1) {
    for (let j = 1; j <= n; j += 1) {
      const cost = s[i - 1] === t[j - 1] ? 0 : 1;
      d[i][j] = Math.min(
        d[i - 1][j] + 1,
        d[i][j - 1] + 1,
        d[i - 1][j - 1] + cost,
      );
      if (
        i > 1
        && j > 1
        && s[i - 1] === t[j - 2]
        && s[i - 2] === t[j - 1]
      ) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
  }
  return d[m][n];
}

function fuzzyThreshold(normalizedLen) {
  return normalizedLen >= 6 ? 2 : 1;
}

function mergeReasonForPair(a, b) {
  const na = normalizeArtistName(a);
  const nb = normalizeArtistName(b);
  if (!na || !nb) return null;
  if (SOLO_ARTISTS.has(na) || SOLO_ARTISTS.has(nb)) return null;

  if (na === nb) return 'exact';

  const sa = normalizeArtistName(stripCollaboratorSuffix(a));
  const sb = normalizeArtistName(stripCollaboratorSuffix(b));
  if (sa && sb && sa === sb) return 'suffix';

  const threshold = Math.min(fuzzyThreshold(na.length), fuzzyThreshold(nb.length));
  if (damerauLevenshtein(na, nb) <= threshold) return 'fuzzy';

  return null;
}

function variantAlbumCount(variant) {
  if (typeof variant?.albumCount === 'number') return variant.albumCount;
  return variant?.albums?.length ?? 0;
}

function pickDisplayName(variants, mergeReasons) {
  const names = variants.map((v) => v.name);
  const maxLen = Math.max(...names.map((n) => n.length));
  const minLen = Math.min(...names.map((n) => n.length));
  const similarLengths = (maxLen - minLen) <= 2;

  const withoutSuffix = variants.filter((v) => !hasCollaboratorSuffix(v.name));

  if (withoutSuffix.length > 0 && mergeReasons.has('suffix')) {
    return [...withoutSuffix].sort((a, b) => {
      if (variantAlbumCount(b) !== variantAlbumCount(a)) return variantAlbumCount(b) - variantAlbumCount(a);
      return a.name.length - b.name.length;
    })[0].name;
  }

  if (mergeReasons.has('fuzzy') && similarLengths) {
    return [...variants].sort((a, b) => {
      if (variantAlbumCount(b) !== variantAlbumCount(a)) return variantAlbumCount(b) - variantAlbumCount(a);
      return b.name.length - a.name.length;
    })[0].name;
  }

  return names.reduce((best, cur) => (cur.length > best.length ? cur : best), names[0]);
}

function pickCatalogFields(variants, displayName, artistCatalog = {}) {
  const catalog = artistCatalog[displayName];
  if (catalog?.image_url) {
    return {
      id: catalog.id || null,
      image_url: catalog.image_url,
      image_source: catalog.image_source || null,
    };
  }

  const withImage = [...variants]
    .filter((v) => artistCatalog[v.name]?.image_url)
    .sort((a, b) => variantAlbumCount(b) - variantAlbumCount(a))[0];

  if (withImage) {
    const c = artistCatalog[withImage.name];
    return {
      id: c?.id || withImage.id || null,
      image_url: c?.image_url || withImage.image_url || null,
      image_source: c?.image_source || withImage.image_source || null,
    };
  }

  const richest = [...variants].sort((a, b) => variantAlbumCount(b) - variantAlbumCount(a))[0];
  return {
    id: richest?.id || catalog?.id || null,
    image_url: richest?.image_url || catalog?.image_url || null,
    image_source: richest?.image_source || catalog?.image_source || null,
  };
}

class UnionFind {
  constructor(n) {
    this.parent = Array.from({ length: n }, (_, i) => i);
  }

  find(x) {
    if (this.parent[x] !== x) this.parent[x] = this.find(this.parent[x]);
    return this.parent[x];
  }

  union(a, b) {
    const ra = this.find(a);
    const rb = this.find(b);
    if (ra !== rb) this.parent[rb] = ra;
  }
}

/**
 * @param {Array<{ name: string, albums: object[], trackCount: number, id?: string, image_url?: string, image_source?: string }>} rawArtists
 * @param {Record<string, object>} [artistCatalog]
 * @returns {Array<{ name: string, variantNames: string[], albums: object[], trackCount: number, id, image_url, image_source, mergeReasons: string[] }>}
 */
export function groupArtistVariants(rawArtists, artistCatalog = {}) {
  if (!Array.isArray(rawArtists) || rawArtists.length === 0) return [];

  const n = rawArtists.length;
  const skipFuzzy = n > 1200;
  const uf = new UnionFind(n);
  const pairReasons = [];

  for (let i = 0; i < n; i += 1) {
    for (let j = i + 1; j < n; j += 1) {
      const reason = mergeReasonForPair(rawArtists[i].name, rawArtists[j].name);
      if (!reason || (reason === 'fuzzy' && skipFuzzy)) continue;
      uf.union(i, j);
      pairReasons.push({ i, j, reason });
    }
  }

  const buckets = new Map();
  for (let i = 0; i < n; i += 1) {
    const root = uf.find(i);
    if (!buckets.has(root)) buckets.set(root, []);
    buckets.get(root).push(i);
  }

  return [...buckets.values()].map((indices) => {
    const variants = indices.map((i) => rawArtists[i]);
    const mergeReasons = new Set();
    const indexSet = new Set(indices);
    for (const { i, j, reason } of pairReasons) {
      if (indexSet.has(i) && indexSet.has(j)) mergeReasons.add(reason);
    }

    const variantNames = variants.map((v) => v.name);
    const sampleAlbums = [];
    const seenSampleKeys = new Set();
    let trackCount = 0;
    let albumCount = 0;

    for (const variant of variants) {
      trackCount += variant.trackCount || 0;
      albumCount += variantAlbumCount(variant);
      const samples = variant.sampleAlbums || variant.albums || [];
      for (const album of samples) {
        const key = album.album_key || album.id;
        if (key && seenSampleKeys.has(key)) continue;
        if (key) seenSampleKeys.add(key);
        if (sampleAlbums.length < 4) sampleAlbums.push(album);
      }
    }

    const displayName = pickDisplayName(variants, mergeReasons);
    const catalogFields = pickCatalogFields(variants, displayName, artistCatalog);

    return {
      name: displayName,
      variantNames,
      albumCount,
      sampleAlbums,
      trackCount,
      ...catalogFields,
      mergeReasons: [...mergeReasons],
    };
  }).sort((a, b) => a.name.localeCompare(b.name));
}

/** Map display name → Set of raw tag names for album filtering. */
export function buildArtistVariantLookup(groupedArtists) {
  const map = new Map();
  for (const artist of groupedArtists || []) {
    map.set(artist.name, new Set(artist.variantNames || [artist.name]));
  }
  return map;
}

let groupedCacheKey = '';
/** @type {Array<object>|null} */
let groupedCache = null;
/** @type {Map<string, Set<string>>|null} */
let variantLookupCache = null;

function buildGroupedCacheKey(rawArtists, catalogFingerprint) {
  const len = rawArtists?.length ?? 0;
  const first = rawArtists?.[0]?.name ?? '';
  const last = len > 0 ? rawArtists[len - 1]?.name ?? '' : '';
  return `${String(catalogFingerprint ?? '')}:${len}:${first}:${last}`;
}

/** Memoized variant grouping — pure fn of catalog revision + raw index. */
export function getGroupedArtistsCached(rawArtists, catalogFingerprint) {
  const key = buildGroupedCacheKey(rawArtists, catalogFingerprint);
  if (groupedCache && groupedCacheKey === key) return groupedCache;
  groupedCache = groupArtistVariants(rawArtists, {});
  groupedCacheKey = key;
  variantLookupCache = buildArtistVariantLookup(groupedCache);
  return groupedCache;
}

export function getArtistVariantLookupCached(rawArtists, catalogFingerprint) {
  getGroupedArtistsCached(rawArtists, catalogFingerprint);
  return variantLookupCache ?? new Map();
}

/** Overlay live catalog portraits onto grouped rows without regrouping. */
export function mergeArtistCatalogOverlay(artist, artistCatalog = {}) {
  if (!artist) return artist;
  const names = artist.variantNames || [artist.name];
  let merged = artist;
  for (const n of names) {
    const cat = artistCatalog?.[n];
    if (!cat) continue;
    merged = {
      ...merged,
      id: merged.id || cat.id || null,
      image_url: merged.image_url || cat.image_url || null,
      image_source: merged.image_source || cat.image_source || null,
      enrichment_attempted_at: merged.enrichment_attempted_at || cat.enrichment_attempted_at || null,
    };
  }
  return merged;
}

/** @internal test helper */
export function resetGroupedArtistsCacheForTests() {
  groupedCacheKey = '';
  groupedCache = null;
  variantLookupCache = null;
}
